import React, { useState, useEffect } from 'react';
import Header from './Header';
import Navigation from './Navigation';
import { useNavigate } from 'react-router-dom';
import api from './services/apiService';
import { authService } from './services/authService';
import { FaCrown } from 'react-icons/fa';
import ProFeatureModal from './components/ProFeatureModal';
import './scss/leaderboard.scss';
import './scss/Dashboard.scss';

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [error, setError] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
  const [teamType, setTeamType] = useState(null);
  const currentPlayerId = sessionStorage.getItem('player_id');
  
  // Get user name from session storage with fallbacks
  const getUserName = () => {
    // Try different possible session storage keys
    let firstName = sessionStorage.getItem('first_name') || 
                   sessionStorage.getItem('firstName') || 
                   sessionStorage.getItem('user_first_name') || 'You';
    
    let lastName = sessionStorage.getItem('last_name') || 
                  sessionStorage.getItem('lastName') || 
                  sessionStorage.getItem('user_last_name') || 'Player';
    
    // Ensure values are strings
    firstName = String(firstName || 'You');
    lastName = String(lastName || 'Player');
    
    // Properly capitalize if the strings have length
    try {
      return {
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase(),
        lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase()
      };
    } catch (error) {
      console.error('Error formatting user name:', error);
      return { firstName: 'You', lastName: 'Player' };
    }
  };
  
  const { firstName: currentUserFirstName, lastName: currentUserLastName } = getUserName();
  
  const [isManager, setIsManager] = useState(false);
  const [showDummyData, setShowDummyData] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useNavigate();
  
  // Check if user is a coach
  const isCoach = sessionStorage.getItem('user_type') === 'coach';

  // Calculate Pro status from API response data (same as MyProfile.js)
  const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
  const isPro = isProFromAPI;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSeasonDropdown && !event.target.closest('.season-selector-wrapper')) {
        setShowSeasonDropdown(false);
      }
    };

    if (showSeasonDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSeasonDropdown]);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const teamId = sessionStorage.getItem('selected_team_id');
        const url = teamId 
          ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
          : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
        
        const response = await api.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          setSubscriptionStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    const fetchPlayerProfile = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_profile.php?player_id=${currentPlayerId}`);
        if (response.data.success) {
          setIsManager(response.data.player?.is_manager || false);
        }
      } catch (error) {
        console.error('Error fetching player profile:', error);
      }
    };

    if (currentPlayerId) {
      fetchSubscriptionStatus();
      fetchPlayerProfile();
    }

    const fetchSeasons = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        if (!teamId) return;
        
        const token = sessionStorage.getItem('token');
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/admin/get_seasons.php?team_id=${teamId}&include_active=true`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const result = response.data;
        
        if (result.success) {
          setSeasons(result.seasons || []);
          // Set current season as default
          if (result.current_season) {
            setSelectedSeason(result.current_season);
          } else if (result.seasons && result.seasons.length > 0) {
            // Find active season or use most recent
            const activeSeason = result.seasons.find(s => s.is_active === 1);
            setSelectedSeason(activeSeason || result.seasons[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
      }
    };
    
    fetchSeasons();
  }, [navigate, currentPlayerId]);

  // Fetch leaderboard when season changes or on initial load
  useEffect(() => {
    const fetchLeaderboard = async () => {
      const selectedTeamId = sessionStorage.getItem('selected_team_id');
      try {
        let url = `${process.env.REACT_APP_API_URL}/get_leaderboard.php?team_id=${selectedTeamId}`;
        
        // Add season_id if selected
        if (selectedSeason?.season_id) {
          url += `&season_id=${selectedSeason.season_id}`;
        }
        
        const response = await api.get(url);
        if (response.data.success) {
          // Filter out removed players when viewing a specific season
          let leaderboardData = response.data.leaderboard || [];
          if (selectedSeason?.season_id) {
            leaderboardData = leaderboardData.filter(player => !player.is_removed);
          }
          
          // Store the leaderboard data
          setLeaderboard(leaderboardData);
          
          // Store team type
          if (response.data.team_type) {
            setTeamType(response.data.team_type);
          }
          
          // Check if there are any games (matches/training) created for the team
          const hasGamesCreated = response.data.has_games || false;
          console.log('hasGamesCreated from leaderboard API:', hasGamesCreated, 'game_count:', response.data.game_count);
          
          // Show dummy data only if NO games have been created
          setShowDummyData(!hasGamesCreated);
          setError(null);
        } else {
          setError('Failed to fetch leaderboard');
          setShowDummyData(true);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          // Token expired or invalid, redirect to login
          authService.clearTokens();
          navigate('/');
        } else {
          setError(error.response?.data?.message || 'Failed to fetch leaderboard');
          setShowDummyData(true);
        }
      }
    };

    if (selectedSeason !== null) {
      fetchLeaderboard();
    }
  }, [selectedSeason, navigate]);



  const handleRowClick = (playerId) => {
    navigate(`/profile/${playerId}`);
  };

  const handleAwardClick = () => {
    // Handle award click
  };

  const shouldBlurPlayer = (index, playerId) => {
    // Managers/coaches should see full leaderboard without restrictions
    if (isManager || isPro || isCoach) return false;
    
    const currentPlayerIndex = leaderboard.findIndex(p => parseInt(p.player_id) === parseInt(currentPlayerId));

    // If we can't find the current player, blur everything
    if (currentPlayerIndex === -1) {
      return true;
    }
    
    // Calculate distance from current player
    const distanceFromCurrent = Math.abs(index - currentPlayerIndex);
    
    // Don't blur if:
    // 1. It's the current player (distance = 0)
    // 2. It's one position above (distance = 1)
    // 3. It's one position below (distance = 1)
    const shouldBlur = distanceFromCurrent > 1;
    
    return shouldBlur;
  };

  return (
    <div className="Leaderboard container">
      <Header />
      <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      <div className="leaderboard-header-section">
        <h1>Leaderboard</h1>
        <div className="season-selector-wrapper">
          {seasons.length > 0 && (
            <div className="season-selector" onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}>
              <span>{selectedSeason?.name || 'Select Season'}</span>
              <span className="dropdown-arrow">{showSeasonDropdown ? '▲' : '▼'}</span>
              {showSeasonDropdown && (
                <div className="season-dropdown">
                  {seasons.map(season => (
                    <div 
                      key={season.season_id}
                      className={`season-option ${selectedSeason?.season_id === season.season_id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSeason(season);
                        setShowSeasonDropdown(false);
                      }}
                    >
                      {season.name}
                      {season.is_active === 1 && <span className="active-badge">Current</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {showDummyData && (
        <div className="dummy-data-notice global-notice">
          <h4>Welcome to FirstWhistle!</h4>
          <p>We're showing example data to help you experience the app. 
          The leaderboard will be populated as players score goals in matches.</p>
        </div>
      )}

      {error && (
        <div className="error-message" style={{
          color: 'red',
          textAlign: 'center',
          margin: '10px 0'
        }}>
          {error}
        </div>
      )}

      {!isPro && !isManager && !isCoach && (
        <div 
          className="pro-feature-notice"
          onClick={() => setShowProModal(true)}
        >
          <FaCrown className="crown-icon" />
          <span>Upgrade to Pro to see the full leaderboard</span>
        </div>
      )}
      


      {/* Modified rendering logic to prioritize dummy data */}
      <div className="leaderboard-table-wrapper">
      {showDummyData ? (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Games</th>
              <th>Goals</th>
              <th>Avg</th>
              <th>POTM</th>
              {teamType === 'friendly' && (
                <>
                  <th className="wdl-column">Wins</th>
                  <th className="wdl-column">Draws</th>
                  <th className="wdl-column">Losses</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Example dummy data for the leaderboard */}
            {(() => {
              // Create example data with the current user
              const examplePlayers = [
                { id: 'current-user', first_name: currentUserFirstName, last_name: currentUserLastName, shirt_number: 9, games: 5, goals: 6, motm: 1, wins: 3, draws: 1, losses: 1, isCurrentUser: true },
                { id: 'dummy-1', first_name: 'John', last_name: 'Smith', shirt_number: 7, games: 5, goals: 7, motm: 2, wins: 4, draws: 0, losses: 1 },
                { id: 'dummy-2', first_name: 'Michael', last_name: 'Williams', shirt_number: 10, games: 5, goals: 3, motm: 0, wins: 2, draws: 2, losses: 1 },
                { id: 'dummy-3', first_name: 'James', last_name: 'Brown', shirt_number: 8, games: 4, goals: 2, motm: 0, wins: 1, draws: 1, losses: 2 },
                { id: 'dummy-4', first_name: 'Robert', last_name: 'Jones', shirt_number: 11, games: 5, goals: 1, motm: 0, wins: 1, draws: 1, losses: 3 }
              ];
              
              // Sort by goals (descending)
              examplePlayers.sort((a, b) => b.goals - a.goals);
              
              return examplePlayers.map((player, index) => {
                const average = player.goals / player.games;
                return (
                  <tr 
                    key={player.id} 
                    className={`player-row ${player.isCurrentUser ? 'current-player' : ''}`}
                  >
                    <td>
                      {`${player.first_name[0]}. ${player.last_name} (${player.shirt_number})`}
                      {index === 0 && '🏆'}
                    </td>
                    <td>{player.games}</td>
                    <td>{player.goals}</td>
                    <td>{average.toFixed(2)}</td>
                    <td>{player.motm}</td>
                    {teamType === 'friendly' && (
                      <>
                        <td className="wdl-column">{player.wins}</td>
                        <td className="wdl-column">{player.draws}</td>
                        <td className="wdl-column">{player.losses}</td>
                      </>
                    )}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      ) : leaderboard.length > 0 ? (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Games</th>
              <th>Goals</th>
              <th>Avg</th>
              <th>POTM</th>
              {teamType === 'friendly' && (
                <>
                  <th className="wdl-column">Wins</th>
                  <th className="wdl-column">Draws</th>
                  <th className="wdl-column">Losses</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((player, index) => {
              const average = player.games_played > 0 ? (player.total_goals / player.games_played).toFixed(2) : '-';
              const isBlurred = shouldBlurPlayer(index, player.player_id);
              
              return (
                <tr 
                  key={player.player_id}
                  onClick={() => handleRowClick(player.player_id)}
                  className={`
                    player-row
                    ${player.player_id == parseInt(currentPlayerId) ? 'current-player' : ''}
                    ${isBlurred ? 'blurred' : ''}
                  `}
                >
                  <td>
                    <span className={player.is_removed ? 'removed-player' : ''}>
                      {`${player.first_name[0]}. ${player.last_name} (${player.shirt_number})`} 
                    </span>
                    {index === 0 && player.total_goals > 0 && '🏆'}
                    {isBlurred && !isPro && !isManager && !isCoach && (
                      <div className="crown-wrapper">
                        <FaCrown className="crown-icon" />
                      </div>
                    )}
                  </td>
                  <td>{player.games_played}</td>
                  <td>{player.total_goals}</td>
                  <td>{isNaN(average) ? '-' : average}</td>
                  <td>{player.motm_count}</td>
                  {teamType === 'friendly' && (
                    <>
                      <td className="wdl-column">{player.wins || 0}</td>
                      <td className="wdl-column">{player.draws || 0}</td>
                      <td className="wdl-column">{player.losses || 0}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : !error && (
        <p style={{textAlign: 'center'}}>No players found. Check back soon...</p>
      )}
      </div>

      <ProFeatureModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        featureName="Full Leaderboard Access"
      />
    </div>
  );
}

export default Leaderboard; 